/**
 * 屏蔽与静音。
 *
 * 两者的缓存影响范围不同：
 * - 屏蔽是双向的，会连带取关、清通知，所以成功后把时间线、通知、
 *   推荐关注全都作废重取；
 * - 静音只影响我自己的时间线与通知，对方无感，作废范围小一些。
 */

import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { usersApi } from '@/api/endpoints'
import { keys } from '@/api/keys'
import type { Page, User } from '@/api/types'
import { useToast } from '@/components/ui/Toast'
import { useT } from '@/i18n'
import { patchUserEverywhere } from '@/lib/cache'

const pageOptions = {
  initialPageParam: undefined as string | undefined,
  getNextPageParam: (last: Page<User>) => last.next_cursor ?? undefined,
}

export function useBlockedUsers() {
  return useInfiniteQuery({
    queryKey: keys.blocks(),
    queryFn: ({ pageParam }) => usersApi.blocked(pageParam),
    ...pageOptions,
  })
}

export function useMutedUsers() {
  return useInfiniteQuery({
    queryKey: keys.mutes(),
    queryFn: ({ pageParam }) => usersApi.muted(pageParam),
    ...pageOptions,
  })
}

export function useBlockToggle() {
  const t = useT()
  const client = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: ({ username, blocking }: { username: string; blocking: boolean }) =>
      blocking ? usersApi.unblock(username) : usersApi.block(username),

    onSuccess: (fresh, { blocking }) => {
      patchUserEverywhere(client, fresh.username, (user) => ({
        ...user,
        counts: fresh.counts,
        viewer: fresh.viewer ?? user.viewer,
      }))
      // 屏蔽会双向取关并清掉通知，影响面大，相关列表一律重取
      client.invalidateQueries({ queryKey: keys.timelineRoot() })
      client.invalidateQueries({ queryKey: keys.notificationsRoot() })
      client.invalidateQueries({ queryKey: keys.searchRoot() })
      client.invalidateQueries({ queryKey: keys.suggestions() })
      client.invalidateQueries({ queryKey: keys.blocks() })
      // 屏蔽会把彼此移出对方的列表（D81）
      client.invalidateQueries({ queryKey: keys.listsRoot() })
      toast.show(
        blocking
          ? t('relations.unblocked', { username: fresh.username })
          : t('relations.blocked', { username: fresh.username }),
      )
    },
    onError: () => toast.error(t('toast.actionFailed')),
  })
}

export function useMuteToggle() {
  const t = useT()
  const client = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: ({ username, muting }: { username: string; muting: boolean }) =>
      muting ? usersApi.unmute(username) : usersApi.mute(username),

    onSuccess: (fresh, { muting }) => {
      patchUserEverywhere(client, fresh.username, (user) => ({
        ...user,
        viewer: fresh.viewer ?? user.viewer,
      }))
      client.invalidateQueries({ queryKey: keys.timelineHome() })
      client.invalidateQueries({ queryKey: keys.notificationsRoot() })
      client.invalidateQueries({ queryKey: keys.mutes() })
      toast.show(
        muting
          ? t('relations.unmuted', { username: fresh.username })
          : t('relations.muted', { username: fresh.username }),
      )
    },
    onError: () => toast.error(t('toast.actionFailed')),
  })
}
